import { useMsal } from "@azure/msal-react";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Container, Row } from "react-bootstrap";

import { axiosGetRequest, axiosPostRequest } from "../../api/ApiClient";
import DialogMessage from "../../components/DialogMessage";
import { InformationModalIcon } from "../../components/Icons";
import ButtonStyles from "../../styles/components/ButtonStyles";
import {
    StyledBody,
    StyledHeader,
    StyledSubTitle,
    StyledTitle,
} from "../../styles/MainStyles";
import {
    StyledBtnBox,
    StyledCardGrey,
    StyledInfoText,
    StyledSmallIcon,
} from "../../styles/pages/CheckinParameters/CheckinParametersStyles";
import { MASK_DATETIME_BR } from "../../utils/constants";
import GenericUtils from "../../utils/GenericUtils";
import CheckinAbsences from "./Forms/CheckinAbsences";
import CheckinRange from "./Forms/CheckinRange";
import CheckinTolerance from "./Forms/CheckinTolerance";
import ShiftsRange from "./Forms/ShiftsRange";

const { BtnPrimary, BtnOutlinePrimary, DefaultButtonLink } = ButtonStyles;

function CheckinParameters() {
    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();

    const [parametroCheckinId, setParametroCheckinId] = useState("");
    const [minutosIniciais, setMinutosIniciais] = useState("");
    const [minutosFinais, setMinutosFinais] = useState("");
    const [horarioInical, setHorarioInicial] = useState("");
    const [horarioFinal, setHorarioFinal] = useState("");
    const [horarioIniManha, setHorarioIniManha] = useState("");
    const [horarioFimManha, setHorarioFimManha] = useState("");
    const [horarioIniTarde, setHorarioIniTarde] = useState("");
    const [horarioFimTarde, setHorarioFimTarde] = useState("");
    const [horarioIniNoite, setHorarioIniNoite] = useState("");
    const [horarioFimNoite, setHorarioFimNoite] = useState("");
    const [qtdzeroadez, setQtdZeroADez] = useState("");
    const [qtdezavinte, setQtdDezAVinte] = useState("");
    const [qtdvinteatrinta, setQtdVinteATrinta] = useState("");
    const [qtdtrintaaquarenta, setQtdTrintaAQuarenta] = useState("");

    const [updateAt, setUpdateAt] = useState("2000-01-01T00:00:00.000Z");
    const [updateBy, setUpdateBy] = useState("");
    const [successMessage, setSuccessMessage] = useState(false);
    const [errorTitle, setErrorTitle] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const getParametrosCheckin = async () => {
        const response = await axiosGetRequest(
            "/docente/v1/parametros-checkin",
            acessToken,
            instance
        );
        const { data } = response;
        if (response.status === 200) {
            try {
                setParametroCheckinId(data.parametroCheckinId);
                setMinutosFinais(data.horaLetivaToleranciaTermino ?? "00:00");
                setMinutosIniciais(data.horaLetivaToleranciaInicio ?? "00:00");
                setHorarioInicial(
                    data.horaNaoLetivaToleranciaInicio ?? "00:00"
                );
                setHorarioFinal(data.horaNaoLetivaToleranciaFim ?? "00:00");
                setHorarioIniManha(data.turnoManhaInicio ?? "00:00");
                setHorarioFimManha(data.turnoManhaTermino ?? "00:00");
                setHorarioIniTarde(data.turnoTardeInicio ?? "00:00");
                setHorarioFimTarde(data.turnoTardeTermino ?? "00:00");
                setHorarioIniNoite(data.turnoNoiteInicio ?? "00:00");
                setHorarioFimNoite(data.turnoNoiteTermino ?? "00:00");
                setQtdZeroADez(data.limiteJustificativaZeroDez ?? 0);
                setQtdDezAVinte(data.limiteJustificativaOnzeVinte ?? 0);
                setQtdVinteATrinta(data.limiteJustificativaVinteUmTrinta ?? 0);
                setQtdTrintaAQuarenta(
                    data.limiteJustificativaTrintaUmQuarenta ?? 0
                );
                setUpdateAt(data.dataUpdate ?? "2000-01-01T00:00:00.000Z");
                setUpdateBy(data.usuarioDocenteIdUpdate?.docente.nome);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
    };

    const handleCloseDialogSuccess = () => {
        setSuccessMessage(false);
    };

    const dialogSuccess = (
        <DialogMessage
            type="success"
            show={Boolean(successMessage)}
            title="Informações enviadas com sucesso!"
            onClose={() => {
                handleCloseDialogSuccess();
            }}
        />
    );

    const dialogError = (
        <DialogMessage
            type="error"
            show={Boolean(errorTitle)}
            title={errorTitle}
            text={errorMessage}
            onClose={() => {
                setErrorTitle("");
                setErrorMessage("");
            }}
        />
    );

    const handleSubmmit = async () => {
        const data = {
            parametroCheckinId,
            horaLetivaToleranciaInicio: minutosIniciais,
            horaLetivaToleranciaTermino: minutosFinais,
            horaNaoLetivaToleranciaInicio: horarioInical,
            horaNaoLetivaToleranciaFim: horarioFinal,
            turnoManhaInicio: horarioIniManha,
            turnoManhaTermino: horarioFimManha,
            turnoTardeInicio: horarioIniTarde,
            turnoTardeTermino: horarioFimTarde,
            turnoNoiteInicio: horarioIniNoite,
            turnoNoiteTermino: horarioFimNoite,
            limiteJustificativaZeroDez: qtdzeroadez,
            limiteJustificativaOnzeVinte: qtdezavinte,
            limiteJustificativaVinteUmTrinta: qtdvinteatrinta,
            limiteJustificativaTrintaUmQuarenta: qtdtrintaaquarenta,
        };

        const response = await axiosPostRequest(
            "/docente/v1/parametros-checkin",
            data,
            acessToken,
            instance
        );

        if (response.status === 201) {
            getParametrosCheckin();
            setSuccessMessage(true);
        } else {
            setErrorTitle("Erro ao enviar informações.");
            setErrorMessage(response.data.details);
        }
    };

    useEffect(() => {
        getParametrosCheckin();
    }, []);

    return (
        <>
            <StyledHeader>
                <StyledTitle>Parametrização de Check-in</StyledTitle>
                <StyledSubTitle>
                    Use esse espaço para definir e configurar permissões de
                    check-in.
                </StyledSubTitle>
            </StyledHeader>
            <StyledBody>
                <StyledCardGrey>
                    <Container fluid>
                        <Row>
                            <StyledInfoText>
                                Minutos de tolerância para check-in de horas
                                letivas
                            </StyledInfoText>
                        </Row>
                        <Row xs="auto">
                            <CheckinTolerance
                                minutosIniciais={minutosIniciais}
                                minutosFinais={minutosFinais}
                                setMinutosIniciais={setMinutosIniciais}
                                setMinutosFinais={setMinutosFinais}
                            />
                        </Row>
                    </Container>
                    <Container fluid>
                        <Row>
                            <StyledInfoText>
                                Horário de início para check-in de horas não
                                letivas
                            </StyledInfoText>
                        </Row>
                        <Row xs="auto">
                            <CheckinRange
                                horarioInical={horarioInical}
                                horarioFinal={horarioFinal}
                                setHorarioInicial={setHorarioInicial}
                                setHorarioFinal={setHorarioFinal}
                            />
                        </Row>
                    </Container>
                    <Container fluid>
                        <Row>
                            <StyledInfoText>Horários dos turnos</StyledInfoText>
                        </Row>
                        <Row xs="auto">
                            <ShiftsRange
                                horarioIniManha={horarioIniManha}
                                horarioFimManha={horarioFimManha}
                                horarioIniTarde={horarioIniTarde}
                                horarioFimTarde={horarioFimTarde}
                                horarioIniNoite={horarioIniNoite}
                                horarioFimNoite={horarioFimNoite}
                                setHorarioIniManha={setHorarioIniManha}
                                setHorarioFimManha={setHorarioFimManha}
                                setHorarioIniTarde={setHorarioIniTarde}
                                setHorarioFimTarde={setHorarioFimTarde}
                                setHorarioIniNoite={setHorarioIniNoite}
                                setHorarioFimNoite={setHorarioFimNoite}
                            />
                        </Row>
                    </Container>
                    <Container fluid>
                        <Row>
                            <StyledInfoText>
                                Quantidade de check-ins semanais não realizados
                                que podem ser justificados
                            </StyledInfoText>
                            <p>
                                Digite abaixo quantos check-ins não realizados
                                podem ser justificados por semana
                            </p>
                        </Row>
                        <Row xs="auto">
                            <CheckinAbsences
                                qtdzeroadez={qtdzeroadez}
                                qtdezavinte={qtdezavinte}
                                qtdvinteatrinta={qtdvinteatrinta}
                                qtdtrintaaquarenta={qtdtrintaaquarenta}
                                setQtdZeroADez={setQtdZeroADez}
                                setQtdDezAVinte={setQtdDezAVinte}
                                setQtdVinteATrinta={setQtdVinteATrinta}
                                setQtdTrintaAQuarenta={setQtdTrintaAQuarenta}
                            />
                        </Row>
                    </Container>
                    <Container fluid>
                        <div className="mt-4 d-flex justify-content-between flex-wrap">
                            <StyledBtnBox>
                                <InformationModalIcon style={StyledSmallIcon} />
                                Atualizado por {updateBy} em{" "}
                                {format(
                                    Date.parse(updateAt),
                                    MASK_DATETIME_BR
                                ).toString()}
                            </StyledBtnBox>
                            <div className="d-flex flex-wrap">
                                <DefaultButtonLink
                                    onClick={getParametrosCheckin}
                                >
                                    Cancelar alterações
                                </DefaultButtonLink>
                                <div style={{ width: 40 }} />
                                <BtnPrimary onClick={handleSubmmit}>
                                    Salvar parâmetro
                                </BtnPrimary>
                            </div>
                        </div>
                    </Container>
                </StyledCardGrey>
                <BtnOutlinePrimary style={{ width: 106, marginTop: 48 }}>
                    Voltar
                </BtnOutlinePrimary>
                {successMessage && dialogSuccess}
                {errorMessage && dialogError}
            </StyledBody>
        </>
    );
}

export default CheckinParameters;
